<template>
  <lb-dialog
    :value="value"
    :heading="title"
    :width="dialogWidth"
    content-class="upsi-details-dialog"
    @input="onInput"
  >
    <template #body>
      <div
        class="upsi-details-dialog__body"
        data-testid="upsi-details-dialog"
        :aria-busy="loading ? 'true' : 'false'"
      >
        <div
          v-if="loading && !hasDetails"
          class="upsi-details-dialog__loading"
          data-testid="upsi-details-dialog__loading"
        >
          <v-progress-circular indeterminate color="primary" />
        </div>
        <template v-else>
          <p v-if="lead" class="upsi-details-dialog__lead">{{ lead }}</p>
          <UpsiSummaryList :rows="summaryRows" />
          <UpsiActivityHistory
            :history="historyRows"
            :filing-reference="filingReference"
            test-id-prefix="upsi-details-dialog"
          />
        </template>
      </div>
    </template>
    <template #actions>
      <div class="upsi-details-dialog__footer">
        <button
          v-if="canPublishAgain"
          type="button"
          class="upsi-details-dialog__publish-again"
          :disabled="loading"
          data-testid="upsi-details-dialog__publish-again-btn"
          @click="onPublishAgain"
        >
          {{ publishAgainLabel }}
        </button>
        <button
          type="button"
          class="upsi-details-dialog__close"
          data-testid="upsi-details-dialog__close-btn"
          @click="onClose"
        >
          {{ closeLabel }}
        </button>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './UpsiDetailsDialog.scss';
import { UpsiActivityHistory, UpsiSummaryList } from '../upsi';
import { UPSI_MESSAGES, UPSI_STAGES } from '../../../../utils/constants';
import { isCycleClosed } from '../../../../utils/helpers';
import {
  UPSI_DIALOG_WIDTH,
  UPSI_EMPTY,
  UPSI_FIELD_LABELS,
  UPSI_NAME_FALLBACK,
  UPSI_SUMMARY_LABELS,
  displayName,
  fillUpsiTemplate,
  formatPublicationDateTime,
  upsiStageLabel,
  upsiStateOf,
} from '../../components/upsi/upsiDialogUtils';

/** Read-only publish request. Publish again is emitted; this dialog does not call the store. */
export default {
  name: 'UpsiDetailsDialog',

  components: {
    UpsiActivityHistory,
    UpsiSummaryList,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    cycle: {
      type: Object,
      default: () => ({}),
    },
    details: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canPublishAgain: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialogWidth: UPSI_DIALOG_WIDTH,
      closeLabel: UPSI_FIELD_LABELS.CLOSE,
      publishAgainLabel: UPSI_FIELD_LABELS.PUBLISH_AGAIN,
    };
  },

  computed: {
    hasDetails() {
      return !!(this.details && typeof this.details === 'object');
    },
    upsiState() {
      return upsiStateOf(this.details);
    },
    isUnpublished() {
      return this.resolvedStage === UPSI_STAGES.UNPUBLISHED;
    },
    cycleIsOpen() {
      return !!(this.cycle && this.cycle.stage != null && !isCycleClosed(this.cycle.stage));
    },
    resolvedStage() {
      if (this.cycleIsOpen) return UPSI_STAGES.UNPUBLISHED;
      if (this.hasDetails && this.details.upsi_state && this.details.upsi_state.stage != null) {
        return Number(this.details.upsi_state.stage);
      }
      if (this.cycle && this.cycle.upsi_state && this.cycle.upsi_state.stage != null) {
        return Number(this.cycle.upsi_state.stage);
      }
      return UPSI_STAGES.UNPUBLISHED;
    },
    title() {
      return UPSI_MESSAGES.DIALOG_TITLE;
    },
    reviewerName() {
      return displayName(this.upsiState.reviewed_by_name, UPSI_NAME_FALLBACK.REVIEWER);
    },
    lead() {
      if (this.resolvedStage === UPSI_STAGES.PUBLISHED) return '';
      return this.banner.message;
    },
    banner() {
      if (this.isUnpublished) {
        return { tone: 'info', message: UPSI_MESSAGES.BANNER_UNPUBLISHED };
      }
      if (this.resolvedStage === UPSI_STAGES.PENDING_APPROVAL) {
        return { tone: 'info', message: UPSI_MESSAGES.BANNER_AWAITING };
      }
      if (this.resolvedStage === UPSI_STAGES.PUBLISHED) {
        return {
          tone: 'success',
          message: fillUpsiTemplate(UPSI_MESSAGES.BANNER_APPROVED, { reviewer: this.reviewerName }),
        };
      }
      return {
        tone: 'info',
        message: fillUpsiTemplate(UPSI_MESSAGES.BANNER_REJECTED, { reviewer: this.reviewerName }),
      };
    },
    cycleName() {
      const fromDetails = this.details && this.details.cycle && this.details.cycle.description;
      const fromCycle = this.cycle && this.cycle.description;
      return fromDetails || fromCycle || UPSI_EMPTY;
    },
    showFilingReference() {
      return this.resolvedStage === UPSI_STAGES.PENDING_APPROVAL
        || this.resolvedStage === UPSI_STAGES.PUBLISHED;
    },
    showPublication() {
      return this.resolvedStage === UPSI_STAGES.PUBLISHED;
    },
    filingReference() {
      return this.upsiState.filing_reference || '';
    },
    summaryRows() {
      const rows = [{ label: UPSI_SUMMARY_LABELS.CYCLE, value: this.cycleName }];
      if (this.showFilingReference) {
        rows.push({
          label: UPSI_SUMMARY_LABELS.FILING_REFERENCE,
          value: this.filingReference || UPSI_EMPTY,
        });
      }
      rows.push({ label: UPSI_SUMMARY_LABELS.UPSI_STATUS, value: upsiStageLabel(this.resolvedStage) });
      if (this.showPublication) {
        rows.push({
          label: UPSI_SUMMARY_LABELS.PUBLICATION_DATE_TIME,
          value: formatPublicationDateTime(this.upsiState.publication_at),
        });
      }
      return rows;
    },
    historyRows() {
      return this.details && Array.isArray(this.details.history) ? this.details.history : [];
    },
  },

  watch: {
    value(open) {
      if (open) {
        console.log('[UpsiDetailsDialog]:', { stage: this.resolvedStage });
      }
    },
  },

  methods: {
    onPublishAgain() {
      console.log('[UpsiDetailsDialog]:', { action: 'publish-again' });
      this.$emit('publish-again');
    },
    onInput(open) {
      this.$emit('input', open);
      if (!open) this.$emit('close');
    },
    onClose() {
      this.onInput(false);
    },
  },
};
</script>
